import { useDispatch, useSelector } from 'react-redux';
import './css/app.css'
import './css/common.css'
import { useState, useEffect } from 'react';
import { change_Current_Location, changeHeaderTitle } from '../store';
import axios from 'axios';
import { Search, Trash2, Plus, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { MessageModal, LoadingModal } from './component/modal/modal';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const fmtNum = (v) => (Number(v) || 0).toLocaleString();
const today = () => dayjs().format("YYYY-MM-DD");

const DEPOSIT_ADMIN_IDS = new Set(
    (process.env.REACT_APP_DEPOSIT_ADMIN_IDS || "")
        .split(",")
        .map(s => s.trim())
        .filter(Boolean)
);

const TYPE_MAP = {
    DEPOSIT:                   { label: "입금",           color: "#1a56db", sign: "+" },
    MERCHANT_PAYOUT:           { label: "가맹점 정산",     color: "#ef4444", sign: "-" },
    AGENT_PAYOUT:              { label: "영업자 정산",     color: "#ef4444", sign: "-" },
    MERCHANT_WALLET_WITHDRAW:  { label: "가맹점 지갑 출금", color: "#ef4444", sign: "-" },
    AGENT_WALLET_WITHDRAW:     { label: "영업자 지갑 출금", color: "#ef4444", sign: "-" },
    COMPANY_TRANSFER:          { label: "본사 송금",       color: "#ef4444", sign: "-" },
    TRX_TRANSFER:              { label: "개별 거래 이체",   color: "#ef4444", sign: "-" },
    DIRECT_SETTLEMENT:         { label: "직접 정산",       color: "#ef4444", sign: "-" },
    DIRECT_SETTLEMENT_PG_FEE:  { label: "직접 정산 - PG 수수료", color: "#ef4444", sign: "-" },
};

const TYPE_OPTIONS = Object.entries(TYPE_MAP).map(([key, v]) => ({ key, label: v.label }));

function PayoutAccountManage() {
    const dispatch = useDispatch();
    const MyData = useSelector(state => state.MyData);
    const canManageDeposit = DEPOSIT_ADMIN_IDS.has(String(MyData?.id));

    // ── 잔액 요약 ──
    const [summaryData, setSummaryData] = useState(null);
    const [summaryError, setSummaryError] = useState("");

    // ── 입금 등록 폼 ──
    const [form, setForm] = useState({
        amount: "",
        deposit_date: today(),
        depositor_name: "",
        memo: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [showForm, setShowForm] = useState(false);

    // ── 내역 ──
    const [list, setList] = useState([]);
    const [page, setPage] = useState(1);
    const [totPage, setTotPage] = useState(1);
    const [totCnt, setTotCnt] = useState(0);
    const [listLoading, setListLoading] = useState(false);

    // ── 필터 ──
    const [startDate, setStartDate] = useState(dayjs().subtract(1, "day").format("YYYY-MM-DD"));
    const [endDate, setEndDate] = useState(today());
    const [selectedTypes, setSelectedTypes] = useState([]);

    const [message, setMessage] = useState("");

    useEffect(() => {
        dispatch(change_Current_Location("지급계좌 관리"));
        dispatch(changeHeaderTitle({ mainTitle: "지급계좌 관리", subTitle: "입출금 통합" }));
        fetchSummary();
        fetchList(1);
    }, []);

    // ── 잔액 요약 조회 ──
    const fetchSummary = async () => {
        setSummaryError("");
        try {
            const response = await axios.get(
                process.env.REACT_APP_API + "/api/master-balance/summary",
                { withCredentials: true }
            );
            const tmp = response?.data;
            if (tmp?.code < 2000) {
                setSummaryData(tmp.data);
            } else {
                setSummaryError(tmp?.msg || "잔액 조회에 실패했습니다.");
            }
        } catch (err) {
            console.error(err);
            setSummaryError("서버 통신 오류가 발생했습니다.");
        }
    };

    // ── 통합 내역 조회 ──
    const fetchList = async (p) => {
        setListLoading(true);
        const start = Date.now();
        try {
            const params = { page: p, limit: 20 };
            if (startDate) params.startDate = startDate;
            if (endDate) params.endDate = endDate;
            if (selectedTypes.length > 0) params.type = selectedTypes.join(",");

            const response = await axios.get(
                process.env.REACT_APP_API + "/api/master-balance/ledger",
                { params, withCredentials: true }
            );
            const tmp = response?.data;
            if (tmp?.code < 2000) {
                setList(tmp.data.list || []);
                setPage(tmp.data.curPage || 1);
                setTotPage(tmp.data.totPage || 1);
                setTotCnt(tmp.data.totCnt || 0);
            } else {
                setMessage({ msg: tmp?.msg || "조회 실패" });
            }
        } catch (err) {
            console.error(err);
            setMessage({ msg: "서버 통신 오류" });
        } finally {
            const elapsed = Date.now() - start;
            if (elapsed < 500) await new Promise(r => setTimeout(r, 500 - elapsed));
            setListLoading(false);
        }
    };

    // ── 입금 등록 ──
    const handleSubmit = async () => {
        if (!form.amount || Number(form.amount) <= 0) {
            setMessage({ msg: "입금 금액을 입력해주세요." });
            return;
        }
        if (!form.deposit_date) {
            setMessage({ msg: "입금 일자를 선택해주세요." });
            return;
        }
        if (!window.confirm(`${fmtNum(form.amount)}원을 입금 등록하시겠습니까?`)) return;

        setSubmitting(true);
        try {
            const response = await axios.post(
                process.env.REACT_APP_API + "/api/master-balance/deposit",
                {
                    amount: Number(form.amount),
                    deposit_date: form.deposit_date,
                    depositor_name: form.depositor_name,
                    memo: form.memo,
                },
                { withCredentials: true }
            );
            const tmp = response?.data;
            if (tmp?.code < 2000) {
                setMessage({ msg: "입금 등록 완료" });
                setForm({ amount: "", deposit_date: today(), depositor_name: "", memo: "" });
                setShowForm(false);
                fetchSummary();
                fetchList(1);
            } else {
                setMessage({ msg: tmp?.msg || "입금 등록 실패" });
            }
        } catch (err) {
            console.error(err);
            setMessage({ msg: "서버 통신 오류" });
        } finally {
            setSubmitting(false);
        }
    };

    // ── 입금 삭제 ──
    const handleDelete = async (item) => {
        if (!window.confirm("해당 입금 내역을 삭제하시겠습니까?")) return;
        try {
            const response = await axios.delete(
                process.env.REACT_APP_API + `/api/master-balance/deposit/${item.id}`,
                { withCredentials: true }
            );
            const tmp = response?.data;
            if (tmp?.code < 2000) {
                setMessage({ msg: "삭제 완료" });
                fetchSummary();
                fetchList(page);
            } else {
                setMessage({ msg: tmp?.msg || "삭제 실패" });
            }
        } catch (err) {
            console.error(err);
            setMessage({ msg: "서버 통신 오류" });
        }
    };

    // ── 타입 필터 토글 ──
    const toggleType = (key) => {
        setSelectedTypes((prev) =>
            prev.includes(key) ? prev.filter((t) => t !== key) : [...prev, key]
        );
    };

    const outflows = summaryData?.outflows || {};
    const outflowItems = [
        { label: "가맹점 지급", value: outflows.merchant_payouts },
        { label: "영업자 지급", value: outflows.agent_payouts },
        { label: "가맹점 지갑 출금", value: outflows.merchant_wallet_withdraws },
        { label: "영업자 지갑 출금", value: outflows.agent_wallet_withdraws },
        { label: "법인 이체", value: outflows.company_transfers },
        { label: "거래 이체", value: outflows.trx_transfers },
        { label: "직접 정산", value: outflows.direct_settlements },
    ];

    const inputStyle = {
        padding: "6px 10px", fontSize: "13px",
        border: "1px solid #d1d5db", borderRadius: "6px",
        outline: "none", background: "#fff",
    };

    const btnStyle = (color) => ({
        display: "inline-flex", alignItems: "center", gap: "4px",
        padding: "6px 14px", fontSize: "13px", fontWeight: "500",
        border: "none", borderRadius: "6px",
        background: color, color: "#fff", cursor: "pointer",
    });

    return (
        <div style={{ padding: "12px 20px", marginTop: "80px", height: "calc(100vh - 80px)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {listLoading ? <LoadingModal /> : ""}
            <MessageModal message={message} />

            {/* ── 잔액 요약 ── */}
            {summaryError && (
                <div style={{
                    padding: "12px 16px", marginBottom: "16px",
                    background: "#fef2f2", border: "1px solid #fecaca",
                    borderRadius: "8px", color: "#dc2626", fontSize: "13px",
                }}>
                    {summaryError}
                </div>
            )}

            {summaryData && (
                <div style={{
                    display: "flex", alignItems: "stretch", gap: "8px",
                    marginBottom: "16px", flexShrink: 0, flexWrap: "wrap",
                }}>
                    <div style={{
                        display: "flex", alignItems: "center", gap: "4px",
                        padding: "8px 12px", background: "#f9fafb", border: "1px solid #e5e7eb",
                        borderRadius: "6px", fontSize: "12px", color: "#6b7280", whiteSpace: "nowrap",
                    }}>
                        기준일 <span style={{ fontWeight: "600", color: "#374151" }}>{summaryData.base_date}</span>
                    </div>
                    <SummaryItem label="기초 잔액" value={summaryData.initial_balance} color="#6366f1" />
                    <SummaryItem label="입금" value={summaryData.deposits_after_base} color="#22c55e" />
                    <SummaryItem label="출금" value={summaryData.total_outflows} color="#ef4444"
                        expandable expandedContent={
                            <div style={{ position: "absolute", top: "100%", left: 0, zIndex: 10, marginTop: "4px", minWidth: "220px",
                                background: "#fff", border: "1px solid #e5e7eb", borderRadius: "8px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                            }}>
                                {outflowItems.map((item, idx) => (
                                    <div key={idx} style={{
                                        display: "flex", justifyContent: "space-between", padding: "6px 12px",
                                        fontSize: "12px", borderBottom: idx < outflowItems.length - 1 ? "1px solid #f3f4f6" : "none",
                                    }}>
                                        <span style={{ color: "#6b7280" }}>{item.label}</span>
                                        <span style={{ fontWeight: "600" }}>{fmtNum(item.value)}원</span>
                                    </div>
                                ))}
                            </div>
                        }
                    />
                    <SummaryItem label="현재 잔액" value={summaryData.balance} color="#1a56db" highlight />
                </div>
            )}

            {/* ── 입금 등록 폼 ── */}
            {canManageDeposit && showForm && (
                <div style={{
                    padding: "20px", marginBottom: "16px", flexShrink: 0,
                    background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "8px",
                }}>
                    <div style={{ fontSize: "14px", fontWeight: "600", marginBottom: "16px" }}>입금 등록</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", marginBottom: "16px" }}>
                        <div>
                            <label style={{ display: "block", fontSize: "12px", color: "#6b7280", marginBottom: "4px" }}>
                                입금 금액 <span style={{ color: "#ef4444" }}>*</span>
                            </label>
                            <input
                                type="number"
                                placeholder="금액 입력"
                                value={form.amount}
                                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                                style={{ ...inputStyle, width: "100%" }}
                            />
                        </div>
                        <div>
                            <label style={{ display: "block", fontSize: "12px", color: "#6b7280", marginBottom: "4px" }}>
                                입금 일자 <span style={{ color: "#ef4444" }}>*</span>
                            </label>
                            <input
                                type="date"
                                value={form.deposit_date}
                                onChange={(e) => setForm({ ...form, deposit_date: e.target.value })}
                                style={{ ...inputStyle, width: "100%" }}
                            />
                        </div>
                        <div>
                            <label style={{ display: "block", fontSize: "12px", color: "#6b7280", marginBottom: "4px" }}>입금자명</label>
                            <input
                                type="text"
                                placeholder="입금자명"
                                value={form.depositor_name}
                                onChange={(e) => setForm({ ...form, depositor_name: e.target.value })}
                                style={{ ...inputStyle, width: "100%" }}
                            />
                        </div>
                        <div>
                            <label style={{ display: "block", fontSize: "12px", color: "#6b7280", marginBottom: "4px" }}>메모</label>
                            <input
                                type="text"
                                placeholder="메모"
                                value={form.memo}
                                onChange={(e) => setForm({ ...form, memo: e.target.value })}
                                style={{ ...inputStyle, width: "100%" }}
                            />
                        </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                        <button onClick={handleSubmit} disabled={submitting} style={btnStyle("#22c55e")}>
                            {submitting ? "처리중..." : "등록"}
                        </button>
                    </div>
                </div>
            )}

            {/* ── 필터 영역 ── */}
            <div style={{
                display: "flex", alignItems: "center", gap: "6px",
                padding: "8px 12px", marginBottom: "10px", flexShrink: 0, flexWrap: "wrap",
                background: "#fff", border: "1px solid #e5e7eb", borderRadius: "8px",
            }}>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={inputStyle} />
                <span style={{ fontSize: "12px", color: "#9ca3af" }}>~</span>
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={inputStyle} />
                <button onClick={() => fetchList(1)} style={btnStyle("#374151")}>
                    <Search size={13} /> 조회
                </button>

                <div style={{ width: "1px", height: "20px", background: "#e5e7eb", margin: "0 2px" }} />

                {TYPE_OPTIONS.map((opt) => {
                    const active = selectedTypes.includes(opt.key);
                    return (
                        <button
                            key={opt.key}
                            onClick={() => toggleType(opt.key)}
                            style={{
                                padding: "2px 8px", fontSize: "11px",
                                borderRadius: "4px", cursor: "pointer",
                                border: active ? "1px solid #1a56db" : "1px solid #e5e7eb",
                                background: active ? "#eff6ff" : "transparent",
                                color: active ? "#1a56db" : "#9ca3af",
                                fontWeight: active ? "600" : "400",
                            }}
                        >
                            {opt.label}
                        </button>
                    );
                })}
                {selectedTypes.length > 0 && (
                    <button
                        onClick={() => setSelectedTypes([])}
                        style={{
                            padding: "2px 8px", fontSize: "11px",
                            borderRadius: "4px", cursor: "pointer",
                            border: "1px solid #fecaca", background: "#fef2f2", color: "#ef4444",
                        }}
                    >
                        초기화
                    </button>
                )}

                <span style={{ fontSize: "12px", color: "#6b7280", marginLeft: "auto" }}>
                    총 <strong style={{ color: "#111" }}>{totCnt}</strong>건
                </span>
                {canManageDeposit && (
                    <button
                        onClick={() => setShowForm(!showForm)}
                        style={btnStyle(showForm ? "#6b7280" : "#1a56db")}
                    >
                        <Plus size={13} />
                        {showForm ? "닫기" : "입금 등록"}
                    </button>
                )}
            </div>

            {/* ── 통합 내역 ── */}
            <div style={{ flex: 1, minHeight: 0, border: "1px solid #e5e7eb", borderRadius: "8px", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                {/* 헤더 */}
                <div style={{
                    display: "flex", alignItems: "center", gap: "12px",
                    padding: "8px 16px", paddingLeft: "19px",
                    background: "#f9fafb", borderBottom: "1px solid #e5e7eb",
                    fontSize: "11px", fontWeight: "600", color: "#6b7280",
                }}>
                    <span style={{ flexShrink: 0, width: "80px", textAlign: "center" }}>구분</span>
                    <span style={{ flexShrink: 0, width: "120px", textAlign: "right" }}>금액</span>
                    <span style={{ flex: 2, minWidth: 0 }}>내용</span>
                    <span style={{ flex: 2, minWidth: 0 }}>거래처</span>
                    <span style={{ flex: 2, minWidth: 0 }}>참조</span>
                    <span style={{ flexShrink: 0, width: "80px" }}>일시</span>
                    <span style={{ flexShrink: 0, width: "24px" }}></span>
                </div>
                <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                    {listLoading ? (
                        <div style={{ padding: "60px", textAlign: "center", color: "#9ca3af" }}>조회 중...</div>
                    ) : list.length === 0 ? (
                        <div style={{ padding: "60px", textAlign: "center", color: "#9ca3af" }}>데이터가 없습니다</div>
                    ) : list.map((item, idx) => {
                        const typeInfo = TYPE_MAP[item.type] || { label: item.type, color: "#6b7280", sign: "" };
                        const isDeposit = item.type === "DEPOSIT";
                        return (
                            <div
                                key={`${item.type}-${item.id}-${idx}`}
                                style={{
                                    display: "flex", alignItems: "center", gap: "12px",
                                    padding: "10px 16px",
                                    borderLeft: `3px solid ${isDeposit ? "#3b82f6" : "#ef4444"}`,
                                    borderBottom: "1px solid #f3f4f6",
                                    background: idx % 2 === 0 ? "#fff" : "#fafbfc",
                                }}
                            >
                                {/* 구분 */}
                                <span style={{
                                    flexShrink: 0, width: "80px", textAlign: "center",
                                    padding: "3px 0", fontSize: "11px", fontWeight: "600",
                                    borderRadius: "4px",
                                    background: isDeposit ? "#eff6ff" : "#fef2f2",
                                    color: typeInfo.color,
                                }}>
                                    {typeInfo.label}
                                </span>

                                {/* 금액 */}
                                <span style={{
                                    flexShrink: 0, width: "120px", textAlign: "right",
                                    fontSize: "13px", fontWeight: "700",
                                    color: typeInfo.color,
                                    fontVariantNumeric: "tabular-nums",
                                }}>
                                    {typeInfo.sign}{fmtNum(item.amount)}원
                                </span>

                                {/* 내용 */}
                                <span style={{ flex: 2, minWidth: 0, fontSize: "13px", color: "#111", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {item.description || "-"}
                                </span>

                                {/* 거래처 */}
                                <span style={{ flex: 2, minWidth: 0, fontSize: "13px", color: "#6b7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {item.counterparty || "-"}
                                </span>

                                {/* 참조 */}
                                <span style={{ flex: 2, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {item.ref_id ? (
                                        <span style={{
                                            padding: "1px 6px", fontSize: "10px",
                                            background: "#e0e7ff", borderRadius: "3px",
                                            color: "#4338ca", fontFamily: "monospace",
                                        }}>
                                            {item.ref_id}
                                        </span>
                                    ) : "-"}
                                </span>

                                {/* 일시 */}
                                <span style={{ flexShrink: 0, width: "80px", fontSize: "12px", color: "#9ca3af", whiteSpace: "nowrap" }}>
                                    {item.created_at ? dayjs.utc(item.created_at).tz("Asia/Seoul").format("MM-DD HH:mm") : "-"}
                                </span>

                                {/* 삭제 */}
                                <div style={{ flexShrink: 0, width: "24px", textAlign: "center" }}>
                                    {isDeposit && canManageDeposit && (
                                        <Trash2
                                            size={14}
                                            style={{ color: "#d1d5db", cursor: "pointer" }}
                                            onClick={() => handleDelete(item)}
                                            onMouseEnter={(e) => e.currentTarget.style.color = "#ef4444"}
                                            onMouseLeave={(e) => e.currentTarget.style.color = "#d1d5db"}
                                        />
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* ── 페이징 ── */}
                {totPage > 1 && (
                    <div style={{
                        display: "flex", justifyContent: "center", alignItems: "center", gap: "8px",
                        padding: "8px", borderTop: "1px solid #e5e7eb", background: "#f9fafb",
                    }}>
                        <button onClick={() => fetchList(page - 1)} disabled={page <= 1}
                            style={{ ...pageBtnStyle, opacity: page <= 1 ? 0.4 : 1 }}>
                            <ChevronLeft size={14} />
                        </button>
                        <span style={{ fontSize: "12px", color: "#6b7280" }}>{page} / {totPage}</span>
                        <button onClick={() => fetchList(page + 1)} disabled={page >= totPage}
                            style={{ ...pageBtnStyle, opacity: page >= totPage ? 0.4 : 1 }}>
                            <ChevronRight size={14} />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

function SummaryItem({ label, value, color, highlight, expandable, expandedContent }) {
    const [open, setOpen] = useState(false);
    return (
        <div
            style={{
                position: "relative",
                display: "flex", alignItems: "center", gap: "8px",
                padding: "8px 14px",
                background: highlight ? color : "#fff",
                border: highlight ? "none" : "1px solid #e5e7eb",
                borderRadius: "6px",
                cursor: expandable ? "pointer" : "default",
                whiteSpace: "nowrap",
            }}
            onClick={expandable ? () => setOpen(!open) : undefined}
            onMouseLeave={expandable ? () => setOpen(false) : undefined}
        >
            <span style={{ fontSize: "12px", color: highlight ? "rgba(255,255,255,0.8)" : "#6b7280" }}>
                {label}
            </span>
            <span style={{ fontSize: "15px", fontWeight: "700", color: highlight ? "#fff" : color }}>
                {fmtNum(value)}원
            </span>
            {expandable && <ChevronDown size={12} style={{ color: highlight ? "#fff" : "#9ca3af" }} />}
            {expandable && open && expandedContent}
        </div>
    );
}

const pageBtnStyle = {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: "32px", height: "32px",
    border: "1px solid #d1d5db", borderRadius: "6px",
    background: "#fff", cursor: "pointer",
};

export default PayoutAccountManage;
